import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { API_URL } from "../config";

const Ticket = () => {
  useEffect(() => {
    document.title = "Tickets | Oriflamme IT Solutions";
  }, []);

  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  //assign
  const userRoles = [1, 2];
  const [reportData, setReportData] = useState(null);
  const [filters, setFilters] = useState({
    filter_date: null,
  });

  const getEngineerScheduleReport = async (params) => {
    const query = new URLSearchParams();

    Object.keys(params).forEach((key) => {
      if (
        params[key] !== null &&
        params[key] !== undefined &&
        params[key] !== ""
      ) {
        if (Array.isArray(params[key])) {
          params[key].forEach((v) => query.append(`${key}[]`, v));
        } else {
          query.append(key, params[key]);
        }
      }
    });

    const res = await fetch(
      `${API_URL}/api/engineer-schedule-report?${query.toString()}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      },
    );

    if (!res.ok) {
      throw new Error("Failed to fetch engineer report");
    }

    return await res.json();
  };

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getEngineerScheduleReport({
        ...filters,
        roles: userRoles,
      });

      if (response.success) {
        setReportData(response.data);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [filters.filter_date]);

  const handleDateChange = (e) => {
    setFilters((prev) => ({
      ...prev,
      filter_date: e.target.value || null,
    }));
  };

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const res = await fetch(`${API_URL}/api/complains`, {
          method: "GET",
          credentials: "include",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });

        if (res.status === 401) {
          navigate("/login");
          return;
        }

        if (!res.ok) throw new Error("Failed to fetch contacts");

        const data = await res.json();
        const contactsData = data.data || [];

        const sortedData = contactsData.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at),
        );

        setContacts(sortedData);
        setFilteredContacts(sortedData);
      } catch (err) {
        setError(err.message);
        setContacts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchContacts();
  }, [navigate]);

  useEffect(() => {
  const filtered = contacts.filter((item) => {
    const searchMatch = [
      item.ticket_no,
      item.company?.company_name,
      item.subject,
      item.engineers1
        ?.map((eng) => `${eng.first_name} ${eng.last_name}`)
        .join(" "),
    ]
      .join(" ")
      .toLowerCase()
      .includes(search.toLowerCase());

    let dateMatch = true;

    if (filters.filter_date) {
      const ticketDate = new Date(item.created_at)
        .toISOString()
        .split("T")[0];

      dateMatch = ticketDate === filters.filter_date;
    }

    return searchMatch && dateMatch;
  });

  setFilteredContacts(filtered);
}, [search, contacts, filters.filter_date]);

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredContacts);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Tickets");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(data, "Tickets.xlsx");
  };

  const columns = [
    {
      name: "Ticket No",
      selector: (row) => `#${row.ticket_no}`,
      sortable: true,
    },
    {
      name: "Eng. Name",
      selector: (row) =>
        row.engineers1
          ?.map((item) => `${item.first_name || ""} ${item.last_name || ""}`)
          .join(", "),
      sortable: true,
    },

    {
      name: "Company Name",
      selector: (row) => row.company?.company_name,
      sortable: true,
    },
    { name: "Issue", selector: (row) => row.subject, sortable: true },
    {
      name: "Complaint Date",
      selector: (row) =>
        new Date(row.created_at).toLocaleString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }),
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => row.complain_status?.name,
      sortable: false,
    },
    {
      name: "Priority",
      selector: (row) => row.priority?.name,
      sortable: false,
    },
  ];

  return (
    <div className="container mt-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <input
          type="text"
          placeholder="Search by ticket no, company, or subject..."
          className="form-control w-50"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <button className="btn btn-warning ms-2" onClick={exportToExcel}>
          Export to Excel
        </button>
        <button
          className="btn btn-success"
          onClick={() => navigate("/ticket/create")}
        >
          New Ticket
        </button>
      </div>
      <div className="row g-3">
        <div className="col-lg-8 card">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"></div>
              <div className="mt-2">Loading...</div>
            </div>
          ) : (
            <>
              {error && (
                <div className="alert alert-danger">Error: {error}</div>
              )}

              <DataTable
                columns={columns}
                data={filteredContacts}
                pagination
                highlightOnHover
                striped
                responsive
              />
            </>
          )}
        </div>
        <div className="col-lg-4 card">
          <div className="engineer-schedule-report">
            {/* Filters */}
            <div className="filters-section">
              <div className="filter-group">
                <label htmlFor="filter_date">Filter Date:</label>
                <input
                  type="date"
                  id="filter_date"
                  value={filters.filter_date || ""}
                  onChange={handleDateChange}
                />
              </div>
              <button onClick={fetchReport} className="refresh-btn">
                Refresh
              </button>
            </div>

            {reportData && (
              <div className="report-container">
                {/* Scheduled Engineers Section */}
                <div className="card scheduled-section">
                  <div className="card-header success-header">
                    <div>
                      <strong>{reportData.report_date}</strong> Scheduled
                      engineers
                      <br />
                      <strong>
                        Total Complaints: {reportData.total_complaints}
                      </strong>
                    </div>
                  </div>

                  <div className="report-content">
                    {reportData.scheduled_engineers.length > 0 ? (
                      reportData.scheduled_engineers.map((engineer) => (
                        <div
                          key={engineer.engineer_id}
                          className="engineer-card"
                        >
                          <table className="engineer-table">
                            <thead>
                              <tr className="engineer-header">
                                <th>
                                  <b>{engineer.engineer_name}</b>
                                </th>
                                <td className="total-calls">
                                  Total Calls - {engineer.total_calls}
                                </td>
                              </tr>
                            </thead>
                            <tbody>
                              {engineer.status_wise_calls.map(
                                (statusItem, idx) => (
                                  <tr key={idx}>
                                    <td>{statusItem.status}</td>
                                    <td>{statusItem.count}</td>
                                  </tr>
                                ),
                              )}
                            </tbody>
                          </table>
                        </div>
                      ))
                    ) : (
                      <div className="no-data-message">
                        No scheduled engineers found for{" "}
                        {reportData.report_date}
                      </div>
                    )}
                  </div>
                </div>

                {/* Not Scheduled Engineers Section */}
                {reportData.can_view_unscheduled && (
                  <div className="card unscheduled-section">
                    <div className="card-header danger-header">
                      {reportData.report_date} Not scheduled engineers
                    </div>

                    <div className="unscheduled-content">
                      {reportData.has_pending_engineers ? (
                        reportData.not_scheduled_engineers.map(
                          (engineer, index) => (
                            <div key={engineer.id} className="pending-engineer">
                              {index + 1}: {engineer.name}
                            </div>
                          ),
                        )
                      ) : (
                        <div className="success-message">
                          I have scheduled a call with all engineers. No one is
                          pending for a call on {reportData.report_date}.
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Ticket;
